import { useState } from "react";
import useStreamVisualizer from "../hooks/useStreamVisualizer";

// Effet Matrix caractère par caractère
function wrapChars(text) {
  return text.split("").map((char, i) => (
    <span
      key={i}
      className="char-glow"
      style={{ animationDelay: `${Math.random() * 2}s` }}
    >
      {char}
    </span>
  ));
}

export default function StreamPage() {
  // États jauges
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [volumeKB, setVolumeKB] = useState(null);

  // États contrôles
  const [tokenLength, setTokenLength] = useState(32);
  const [capacityKB, setCapacityKB] = useState(1024);
  const [charOptions, setCharOptions] = useState({
    lowercase: true,
    uppercase: true,
    numbers: true,
    symbols: false,
  });
  const [running, setRunning] = useState(false);

  // Vitesse indépendante + globale
  const [speedFill, setSpeedFill] = useState(1.0);
  const [speedOutline, setSpeedOutline] = useState(1.0);
  const [speedMultiplier, setSpeedMultiplier] = useState(1.0);

  // Flux de tokens
  const [tokens, setTokens] = useState([]);

  // Hook StreamVisualizer
  const { containerRef } = useStreamVisualizer({
    tokenLength,
    capacityKB,
    charOptions,
    running,
    speedFill,
    speedOutline,
    speedMultiplier,
    onTokensUpdate: (newTokens) =>
      setTokens((prev) => {
        const updated = [...prev, ...newTokens];
        return updated.slice(-20); // garde les 20 derniers
      }),
    onEntropyUpdate: (val) => {
      if (val.entropy_ui !== undefined) setEntropyUI(val.entropy_ui);
      if (val.entropy_dyn !== undefined) setEntropyDyn(val.entropy_dyn);
      if (val.volume_kb !== undefined) setVolumeKB(val.volume_kb);
    },
  });

  // Score total
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div className="page-container">
      <h1>Stream Visualizer</h1>

      {/* Jauges */}
      <div className="gauges-row">
        <div className="card small">
          <h2>Entropie UI</h2>
          <div className="value ui">
            {entropyUI !== null ? entropyUI.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div className="value dyn">
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#bb86fc" }}>Score Total</h2>
          <div className="value total">{entropyTotal}</div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#03dac6" }}>Volume (Ko)</h2>
          <div className="value">{volumeKB !== null ? volumeKB : "⏳"}</div>
        </div>
      </div>

      {/* Layout principal : Matrix + Controls à gauche | Canvas à droite */}
      <div className="controls-row">
        <div className="controls-panel">
          {/* Flux tokens Matrix */}
          <div className="tokens-container">
            {tokens.map((t, i) => (
              <div key={i} className="token-line">
                {wrapChars(t)}
              </div>
            ))}
          </div>

          {/* Bouton start/stop */}
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Stream" : "▶ Start Stream"}
          </button>

          {/* ===================== */}
          {/* Groupe Tokens */}
          {/* ===================== */}
          <fieldset className="control-group">
            <legend>⚡ Tokens</legend>
            <label>Longueur Token : {tokenLength}</label>
            <input
              type="range"
              min={8}
              max={128}
              value={tokenLength}
              onChange={(e) => setTokenLength(Number(e.target.value))}
            />
            <label>Capacité (Ko) : {capacityKB}</label>
            <input
              type="range"
              min={1}
              max={10240}
              value={capacityKB}
              onChange={(e) => setCapacityKB(Number(e.target.value))}
            />
          </fieldset>

          {/* ===================== */}
          {/* Groupe Vitesse */}
          {/* ===================== */}
          <fieldset className="control-group">
            <legend>🎛 Vitesse</legend>
            <label>
              Cube Bleu (Remplissage) : {speedFill.toFixed(2)}
            </label>
            <input
              type="range"
              min={0.1}
              max={5}
              step={0.1}
              value={speedFill}
              onChange={(e) => setSpeedFill(Number(e.target.value))}
            />
            <label>
              Cube Vert (Filaire) : {speedOutline.toFixed(2)}
            </label>
            <input
              type="range"
              min={0.1}
              max={5}
              step={0.1}
              value={speedOutline}
              onChange={(e) => setSpeedOutline(Number(e.target.value))}
            />
            <label>
              Multiplicateur Global : {speedMultiplier.toFixed(2)}
            </label>
            <input
              type="range"
              min={0.1}
              max={10}
              step={0.1}
              value={speedMultiplier}
              onChange={(e) => setSpeedMultiplier(Number(e.target.value))}
            />
          </fieldset>

          {/* ===================== */}
          {/* Groupe Caractères */}
          {/* ===================== */}
          <fieldset className="control-group">
            <legend>🔠 Caractères</legend>
            <label>
              <input
                type="checkbox"
                checked={charOptions.lowercase}
                onChange={(e) =>
                  setCharOptions({ ...charOptions, lowercase: e.target.checked })
                }
              />
              Minuscules
            </label>
            <label>
              <input
                type="checkbox"
                checked={charOptions.uppercase}
                onChange={(e) =>
                  setCharOptions({ ...charOptions, uppercase: e.target.checked })
                }
              />
              Majuscules
            </label>
            <label>
              <input
                type="checkbox"
                checked={charOptions.numbers}
                onChange={(e) =>
                  setCharOptions({ ...charOptions, numbers: e.target.checked })
                }
              />
              Nombres
            </label>
            <label>
              <input
                type="checkbox"
                checked={charOptions.symbols}
                onChange={(e) =>
                  setCharOptions({ ...charOptions, symbols: e.target.checked })
                }
              />
              Symboles
            </label>
          </fieldset>
        </div>

        {/* Visualizer 3D */}
        <div ref={containerRef} id="stream-3d" className="stream-canvas"></div>
      </div>
    </div>
  );
}
